import React from 'react';
import { Badge, Box, Group, Table, Text } from '@mantine/core';
import { IconBaseline, IconCalendarEvent } from '@tabler/icons-react';
import type { TaskSnapshot } from '../../../stores/types';
import { formatGanttDateFromStored } from '../../../gantt/ganttDateFormat';
import {
  computeCurrentVsBaselineVariance,
  formatVarianceDays,
  getTaskBaseline,
} from '../../../gantt/baselineCompareUtils';

export interface TaskBaselineComparisonSectionProps {
  draft: TaskSnapshot;
  compareBaselineIndex: number;
}

type VarianceTone = 'late' | 'early' | 'even' | 'na';

function varianceTone(days: number | null | undefined): VarianceTone {
  if (days == null || !Number.isFinite(days)) return 'na';
  if (days > 0) return 'late';
  if (days < 0) return 'early';
  return 'even';
}

function varianceBadgeColor(tone: VarianceTone): string {
  if (tone === 'late') return 'red';
  if (tone === 'early') return 'teal';
  return 'gray';
}

function formatDurationLabel(duration: number | null | undefined): string {
  if (duration == null || !Number.isFinite(duration)) return '—';
  return `${Math.round(duration)}d`;
}

function VarianceBadge({ days }: { days: number | null | undefined }) {
  const tone = varianceTone(days);
  return (
    <Badge
      size="sm"
      variant="light"
      color={varianceBadgeColor(tone)}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {formatVarianceDays(days)}
    </Badge>
  );
}

export function TaskBaselineComparisonSection({
  draft,
  compareBaselineIndex,
}: TaskBaselineComparisonSectionProps) {
  const baselineEntry = getTaskBaseline(draft, compareBaselineIndex);
  const variance = computeCurrentVsBaselineVariance(draft, compareBaselineIndex);
  const baselineLabel = baselineEntry?.name?.trim() || `Baseline ${compareBaselineIndex}`;

  const rows = [
    {
      key: 'start',
      label: 'Start',
      current: formatGanttDateFromStored(draft.start) || '—',
      baseline: formatGanttDateFromStored(baselineEntry?.start) || '—',
      varianceDays: variance.startDays,
    },
    {
      key: 'finish',
      label: 'Finish',
      current: formatGanttDateFromStored(draft.finish) || '—',
      baseline: formatGanttDateFromStored(baselineEntry?.finish) || '—',
      varianceDays: variance.finishDays,
    },
    {
      key: 'duration',
      label: 'Duration',
      current: formatDurationLabel(draft.duration),
      baseline: formatDurationLabel(baselineEntry?.duration),
      varianceDays: variance.durationDays,
    },
  ];

  return (
    <Box
      mt="lg"
      p="md"
      style={{
        borderRadius: 10,
        border: '1px solid var(--mantine-color-default-border)',
        background:
          'color-mix(in srgb, var(--mantine-color-brand-1, #e7f5ff) 45%, var(--mantine-color-body))',
      }}
    >
      <Group justify="space-between" align="flex-start" gap="sm" mb="sm" wrap="wrap">
        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
          <Box
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 8,
              flexShrink: 0,
              background: 'var(--mantine-color-body)',
              border: '1px solid var(--mantine-color-default-border)',
              color: 'var(--mantine-color-brand-7, #1c7ed6)',
            }}
          >
            <IconBaseline size={15} stroke={1.75} />
          </Box>
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={700} lh={1.25}>
              Baseline comparison
            </Text>
            <Text size="xs" c="dimmed" mt={2}>
              Current schedule vs {baselineLabel}
            </Text>
          </Box>
        </Group>
        <Badge
          size="sm"
          variant="light"
          color="brand"
          leftSection={<IconCalendarEvent size={12} stroke={1.75} />}
          style={{ flexShrink: 0 }}
        >
          {baselineLabel}
        </Badge>
      </Group>

      {baselineEntry ? (
        <Box
          style={{
            overflow: 'hidden',
            borderRadius: 8,
            border: '1px solid var(--mantine-color-default-border)',
            background: 'var(--mantine-color-body)',
          }}
        >
          <Table
            horizontalSpacing="md"
            verticalSpacing={10}
            withRowBorders
            style={{ tableLayout: 'fixed' }}
          >
            <Table.Thead>
              <Table.Tr
                style={{
                  background:
                    'color-mix(in srgb, var(--mantine-color-default-hover, #f1f3f5) 80%, transparent)',
                }}
              >
                <Table.Th style={{ width: '22%' }}>
                  <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
                    Field
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
                    Baseline
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
                    Current
                  </Text>
                </Table.Th>
                <Table.Th style={{ width: '22%' }}>
                  <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
                    Variance
                  </Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.key}>
                  <Table.Td>
                    <Text size="xs" c="dimmed" fw={600}>
                      {row.label}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {row.baseline}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {row.current}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <VarianceBadge days={row.varianceDays} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      ) : (
        <Text size="sm" c="dimmed">
          This task has no dates saved for {baselineLabel}.
        </Text>
      )}
    </Box>
  );
}
